import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

// 네이티브 <input type="time"> 의 브라우저 기본 피커(오전/오후 휠)를 앱 공통 콤보박스로 대체.
// - 숫자 직접 입력(문자 차단) + 드롭다운 선택 모두 지원.
// - "HH:MM" 문자열로 값을 주고받는다. (초가 붙은 "HH:MM:SS" 로드도 허용 — 초 무시)
const HOUR_ITEMS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
// 분 드롭다운은 5분 스텝(간결). 직접 입력은 0~59 자유.
const MINUTE_ITEMS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

// 숫자만 남기고 2자리로 제한 + 상한 클램프. 미입력은 ''.
function sanitize(raw, max) {
  const digits = String(raw).replace(/\D/g, '').slice(0, 2);
  if (digits === '') return '';
  return String(Math.min(Number(digits), max));
}

/**
 * 시 또는 분 단위 입력 1칸 — 숫자 입력 + 드롭다운.
 * 타이핑 중에는 로컬 draft 로 자유 입력(자릿수 패딩 안 함), blur/선택 시 2자리로 commit.
 */
function TimeUnitField({ value, onCommit, items, max, ariaLabel }) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState({});
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // 외부에서 value 가 바뀌면(포커스 아닐 때만) draft 동기화 — 타이핑 중 끊김 방지.
  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  const openDropdown = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const MAXH = 200;
    const GAP = 4;
    const below = window.innerHeight - r.bottom;
    const above = r.top;
    const placeUp = below < MAXH && above > below;
    setDrop(
      placeUp
        ? { bottom: window.innerHeight - r.top + GAP, left: r.left, width: r.width, maxHeight: Math.min(MAXH, above - GAP - 8) }
        : { top: r.bottom + GAP, left: r.left, width: r.width, maxHeight: Math.min(MAXH, below - GAP - 8) },
    );
    setOpen(true);
  };

  // 바깥 클릭 / Esc 로 닫기
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = (e) => { if (listRef.current?.contains(e.target)) return; setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', () => setOpen(false));
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const commit = (raw) => {
    const v = raw === '' ? '' : String(raw).padStart(2, '0');
    setDraft(v);
    onCommit(v);
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        aria-label={ariaLabel}
        value={draft}
        placeholder="--"
        onFocus={() => { setFocused(true); openDropdown(); }}
        onChange={(e) => setDraft(sanitize(e.target.value, max))}
        onBlur={() => { setFocused(false); commit(draft); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.blur(); } }}
        className="w-full h-9 pl-2.5 pr-6 text-sm tabular-nums bg-white border rounded-lg outline-none transition
          border-mono-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={`${ariaLabel} 선택`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => { if (open) { setOpen(false); } else { inputRef.current?.focus(); openDropdown(); } }}
        className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5"
      >
        <Icon name="chevron-down" size={14} className={`transition-transform ${open ? 'rotate-180 text-orange-500' : 'text-mono-400'}`} />
      </button>

      {open && createPortal(
        <div
          ref={listRef}
          style={{ position: 'fixed', top: drop.top, bottom: drop.bottom, left: drop.left, minWidth: Math.max(drop.width || 0, 56), zIndex: 9999 }}
          className="bg-white border border-mono-200 rounded-lg shadow-lg overflow-y-auto py-1 custom-scrollbar"
        >
          <div style={{ maxHeight: drop.maxHeight || 200 }} className="overflow-y-auto overscroll-contain">
            {items.map((it) => (
              <button
                key={it}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { commit(it); setOpen(false); }}
                className={`w-full text-center px-2 py-1.5 text-sm tabular-nums transition-colors ${
                  it === draft ? 'bg-orange-50 text-orange-500 font-semibold' : 'text-mono-900 hover:bg-orange-50/50'
                }`}
              >
                {it}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * TimePicker — "HH:MM" 시각 선택 (시/분 콤보박스 조합).
 *
 * @param {string} value - "HH:MM" 또는 ""
 * @param {(v:string)=>void} onChange - "HH:MM" emit. 둘 다 비면 "" emit.
 * @param {string} [className]
 */
export default function TimePicker({ value = '', onChange, className = '' }) {
  const [h = '', m = ''] = (value || '').split(':');

  const emit = (nextH, nextM) => {
    if (!nextH && !nextM) { onChange(''); return; }
    onChange(`${(nextH || '0').padStart(2, '0')}:${(nextM || '0').padStart(2, '0')}`);
  };

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <div className="w-14 sm:w-16">
        <TimeUnitField value={h} onCommit={(v) => emit(v, m)} items={HOUR_ITEMS} max={23} ariaLabel="시" />
      </div>
      <span className="text-2sm font-bold text-mono-300">:</span>
      <div className="w-14 sm:w-16">
        <TimeUnitField value={m} onCommit={(v) => emit(h, v)} items={MINUTE_ITEMS} max={59} ariaLabel="분" />
      </div>
    </div>
  );
}
